import { Alert, Select, Text } from '@mantine/core';
import type { ComboboxItem } from '@mantine/core';
import type { AdPlatform, Line, Meta } from '@marketing-tool/shared';
import { useMemo } from 'react';

import { useAdGroups } from '../../data/game-report';
import type { AdGroup } from '../../data/game-report/types';
import * as remote from '../../data/remote-data';
import { formatDeliveryStatus } from '../../lib/status';
import type { Status } from '../../lib/status';

const groupByDeliveryStatus = <
    T extends { id: string | number; name: string } & (
        | { deliveryStatus: Line.DeliveryStatus }
        | { effectiveStatus: Meta.EffectiveStatus }
    ),
>(
    items: T[],
): { group: string; items: ComboboxItem[] }[] => {
    const targets: {
        [key in Status]?: ComboboxItem[];
    } = {};

    for (const item of items) {
        const status = 'deliveryStatus' in item ? item.deliveryStatus : item.effectiveStatus;
        targets[status] ??= [];
        const target = targets[status];
        target.push({ value: item.id.toString(), label: item.name });
    }

    const groups: { group: string; items: ComboboxItem[] }[] = [];
    Object.entries(targets).forEach(([deliveryStatus, items]) => {
        if (items.length) {
            groups.push({
                group: formatDeliveryStatus(deliveryStatus as keyof typeof targets),
                items,
            });
        }
    });
    return groups;
};

export const AdGroupSelector = ({
    id,
    gameId,
    platform,
    campaignId,
    value,
    onChange,
    label,
    clearable,
}: {
    id: string;
    gameId: string;
    platform: AdPlatform;
    campaignId: string | number;
    value?: string | number;
    onChange: (adGroup: AdGroup | undefined) => void;
    label?: string;
    clearable?: boolean;
}) => {
    const adGroups = useAdGroups(gameId, platform, campaignId);
    const groups = useMemo(() => remote.map(adGroups.data, groupByDeliveryStatus), [adGroups.data]);
    return remote.matchAll(groups, {
        NotAsked: () => null,
        Loading: () => (
            <Text c="dimmed" fs="italic">
                Loading ad groups...
            </Text>
        ),
        Failure: (error) => (
            <Alert color="red" title="Error loading ad groups">
                {error}
            </Alert>
        ),
        Success: (groups) => (
            <Select
                id={id}
                label={label}
                value={value?.toString() ?? null}
                onChange={(id) => {
                    if (!id) {
                        onChange(undefined);
                        return;
                    }
                    const adGroup = remote
                        .withDefault(adGroups.data, [])
                        .find((adGroup) => adGroup.id.toString() === id);
                    adGroup && onChange(adGroup);
                }}
                data={groups}
                placeholder="Select an ad group"
                clearable={clearable}
            />
        ),
    });
};
